import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { candidateApi, formatApiError } from "@/lib/apiClient";
import { markAuthed, markAnon, shouldCheckSession } from "@/lib/authMarker";

const CandidateCtx = createContext(null);

export function CandidateAuthProvider({ children }) {
    const [candidate, setCandidate] = useState(null); // null=checking, false=not authed, object=candidate
    const [loading, setLoading] = useState(true);

    const check = useCallback(async () => {
        // Sprint 1 (2026-02-11) — anonymous-visitor gate. Skip the
        // `/api/candidates/me` network round-trip for definitely-
        // logged-out visitors. Shared marker with the employer
        // AuthContext — a candidate session and an employer session
        // are mutually exclusive on the same browser (different
        // cookie namespaces), and the marker tracks EITHER kind of
        // session, so a stale ANON marker from a prior anonymous
        // visit is refreshed the moment the candidate logs in.
        if (!shouldCheckSession()) {
            setCandidate(false);
            setLoading(false);
            return;
        }
        try {
            const me = await candidateApi.me();
            setCandidate(me);
            markAuthed();
        } catch {
            setCandidate(false);
            markAnon();
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        check();
    }, [check]);

    const login = async (email, password) => {
        const res = await candidateApi.login(email, password);
        setCandidate(res.candidate);
        markAuthed();
        return res.candidate;
    };

    const logout = async () => {
        try {
            await candidateApi.logout();
        } catch {
            // ignore
        }
        setCandidate(false);
        markAnon();
    };

    const refresh = async () => {
        try {
            const me = await candidateApi.me();
            setCandidate(me);
            markAuthed();
            return me;
        } catch {
            setCandidate(false);
            markAnon();
            return null;
        }
    };

    // Sprint 1 (2026-02-11) — wrap the exposed setter so external
    // callers that assign a candidate directly (registration flows,
    // OTP verify, magic-link callback) also update the auth marker.
    const setCandidateDirect = (c) => {
        setCandidate(c);
        if (c && typeof c === "object") markAuthed();
        else if (c === false || c === null) markAnon();
    };

    return (
        <CandidateCtx.Provider
            value={{
                candidate,
                loading,
                login,
                logout,
                refresh,
                setCandidate: setCandidateDirect,
                formatError: formatApiError,
            }}
        >
            {children}
        </CandidateCtx.Provider>
    );
}

export function useCandidateAuth() {
    const ctx = useContext(CandidateCtx);
    if (!ctx) throw new Error("useCandidateAuth must be used within CandidateAuthProvider");
    return ctx;
}
